'use client'
import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { FormEvoIcon } from '@repo/ui/FormEvoIcon'
import { useEffect, useState } from 'react'
import { useThirdPartyFormsStore } from '@repo/store/useThirdPartyFormsStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

export const FormEvoSetup = () => {
  const { getFormEvoLoginUrl, hasFormEvoAccessTokenByCurrentUser } = useThirdPartyFormsStore()
  const { queryParams } = useUrlStore()
  // Set when the user is sent here after a 498 (expired FormEvo token). In that case the
  // backend still reports a token, so we must offer a reconnect option regardless.
  const isReauthenticating = queryParams?.reauthenticate === 'true'
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await hasFormEvoAccessTokenByCurrentUser()
        setIsConnected(response === true || response?.hasToken === true || response?.success === true)
      } catch {
        setIsConnected(false)
      } finally {
        setIsLoading(false)
      }
    }
    checkConnection()
  }, [hasFormEvoAccessTokenByCurrentUser])

  const startFormEvoIntegration = async () => {
    const authUrl = await getFormEvoLoginUrl()
    location.replace(authUrl)
  }

  // When reauthenticating we show the reconnect view immediately and ignore the
  // connection check (it would report the stale, expired token as "connected").
  if (isLoading && !isReauthenticating) return null

  return (
    <MUIStack
      data-testid="FormEvoSetup"
      gap={6}
      sx={{ maxWidth: 680, margin: 'auto', backgroundColor: 'white', borderRadius: '6px', padding: '60px 40px' }}
    >
      <FormEvoIcon sx={{ width: 220, height: 55 }} />

      {isReauthenticating ? (
        <>
          <MUITypography variant="body1">
            {`Your FormEvo session has expired. Please reconnect to continue accessing FormEvo forms within Osprey.`}
          </MUITypography>

          <MUITypography variant="body1">
            {`When you click the button below, you'll be redirected to a FormEvo login page. You'll need to grant permission to FormEvo. Once you've done that, you'll be redirected back to Osprey.`}
          </MUITypography>

          <MUIButton
            variant="contained"
            color="primary"
            size="large"
            onClick={startFormEvoIntegration}
            sx={{ maxWidth: 160, width: '100%', margin: '20px 0 0 auto' }}
          >
            Reconnect
            <ChevronRightIcon sx={{ color: 'white' }} />
          </MUIButton>
        </>
      ) : isConnected ? (
        <MUITypography variant="body1">
          {`You are already connected to FormEvo. You can now access FormEvo forms within Osprey.`}
        </MUITypography>
      ) : (
        <>
          <MUITypography variant="body1">
            {`This screen enables you to set up a FormEvo integration, which will allow you to access FormEvo forms within Osprey.`}
          </MUITypography>

          <MUITypography variant="body1">
            {`When you click the button below, you'll be redirected to a FormEvo login page. You'll need to grant permission to FormEvo. Once you've done that, you'll be redirected back to Osprey.`}
          </MUITypography>

          <MUIButton
            variant="contained"
            color="primary"
            size="large"
            onClick={startFormEvoIntegration}
            sx={{ maxWidth: 160, width: '100%', margin: '20px 0 0 auto' }}
          >
            Start
            <ChevronRightIcon sx={{ color: 'white' }} />
          </MUIButton>
        </>
      )}
    </MUIStack>
  )
}
